<template>
  <div class="google-login-wrapper">
    <button 
      @click="handleGoogleLogin" 
      :disabled="loading || !initialized"
      class="google-login-btn"
      type="button"
    >
      <span v-if="!loading && initialized" class="google-icon">
        <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          <path fill="none" d="M0 0h48v48H0z"/>
        </svg>
      </span>
      <span v-if="loading || !initialized" class="spinner"></span>
      <span class="button-text">
        {{ loading ? 'Loading...' : (!initialized ? 'Initializing...' : buttonText) }}
      </span>
    </button>
  </div>
</template>

<script>
import googleAuthService from '@/services/googleAuth';
import { Capacitor } from '@capacitor/core';

export default {
  name: 'GoogleLoginButton',
  
  props: {
    buttonText: {
      type: String,
      default: 'Sign in with Google'
    },
    // Base64 url redirect integrasi eksternal (parameter `ref` di URL)
    refParam: {
      type: String,
      default: ''
    },
    // Merchant id dari URL. Bersama ref: user baru dibuat langsung tanpa form.
    merchantId: {
      type: String,
      default: ''
    }
  },
  
  data() {
    return {
      loading: false,
      error: null,
      initialized: false,
      initializationAttempts: 0,
      maxInitAttempts: 3
    };
  },
  
  computed: {
    isNative() {
      return Capacitor.isNativePlatform();
    }
  },
  
  async mounted() {
    // Component ini hanya untuk native platform
    // Untuk web, gunakan button HTML biasa dengan loginWithGoogle()
    if (!Capacitor.isNativePlatform()) {
      console.warn('GoogleLoginButton component should only be used on native platforms');
      return;
    }
    
    // Inisialisasi dengan retry mechanism
    await this.initializeWithRetry();
  },
  
  methods: {
    async initializeWithRetry(maxRetries = 3) {
      this.initializationAttempts++;
      
      if (this.initializationAttempts > this.maxInitAttempts) {
        this.error = 'Failed to initialize Google Login after multiple attempts. Please refresh the page.';
        return;
      }
      
      for (let i = 0; i < maxRetries; i++) {
        try {
          await googleAuthService.initialize();
          this.initialized = true;
          this.error = null;
          return;
        } catch (error) {
          console.error(`Failed to initialize Google Login (attempt ${i + 1}/${maxRetries}):`, error);
          
          if (i === maxRetries - 1) {
            // Last attempt failed
            this.error = 'Failed to initialize Google Login. Please refresh the page.';
            this.initialized = false;
          } else {
            // Wait before retry (exponential backoff)
            const waitTime = Math.min(1000 * Math.pow(2, i), 5000);
            await new Promise(resolve => setTimeout(resolve, waitTime));
          }
        }
      }
    },
    
    async retryInitialization() {
      this.error = null;
      this.initializationAttempts = 0;
      await this.initializeWithRetry();
    },
    
    async handleGoogleLogin() {
      // Clear previous error
      this.error = null;
      
      if (!this.initialized) {
        console.log('Google Login not initialized, attempting to initialize...');
        await this.initializeWithRetry(1);
        
        if (!this.initialized) {
          this.error = 'Google Login not ready. Please wait a moment and try again.';
          return;
        }
      }
      
      this.loading = true;
      
      try {
        console.log('Starting Google login...');
        // Login dengan Google menggunakan Capacitor plugin (native only)
        const authData = await googleAuthService.login({ ref: this.refParam || null, merchantId: this.merchantId || null });
        console.log('Google login successful, emitting login-success event');
        this.$emit('login-success', authData);
        
      } catch (error) {
        console.error('Google login error:', error);
        this.error = error.message || 'Failed to login with Google';
        this.$emit('login-error', error);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.google-login-wrapper {
  width: 100%;
}

.google-login-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 24px;
  background-color: #ffffff;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #3c4043;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 12px;
}

.google-login-btn:hover:not(:disabled) {
  background-color: #f8f9fa;
  box-shadow: 0 1px 2px 0 rgba(60, 64, 67, 0.3), 0 1px 3px 1px rgba(60, 64, 67, 0.15);
}

.google-login-btn:active:not(:disabled) {
  background-color: #f1f3f4;
}

.google-login-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.google-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.button-text {
  flex: 1;
  text-align: center;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>